import React, { useState, useRef, useEffect } from 'react';
import { useImage } from '../../context/ImageContext';
import { FaInfoCircle } from 'react-icons/fa';

const AdjustPositionDropdown = ({ containerRef, imageRef, isOpen, setIsOpen, aspectRatio }) => {
  const [showInfo, setShowInfo] = useState(false);
  const { selectedImage } = useImage();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });



  const resetImagePosition = () => {
    setPosition({ x: 0, y: 0 });
    setScale(1);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const rect = containerRef.current.getBoundingClientRect();
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const rect = containerRef.current.getBoundingClientRect();
    const newX = e.clientX - startPos.x;
    const newY = e.clientY - startPos.y;

    // Calculate bounds based on image size and scale
    const imageWidth = imageRef.current.naturalWidth * scale;
    const imageHeight = imageRef.current.naturalHeight * scale;
    const maxX = Math.max(0, (imageWidth - rect.width) / 2);
    const maxY = Math.max(0, (imageHeight - rect.height) / 2);

    setPosition({
      x: Math.max(-maxX, Math.min(maxX, newX)),
      y: Math.max(-maxY, Math.min(maxY, newY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    if (e.target === imageRef.current || e.target === containerRef.current) {
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      const newScale = Math.max(0.2, Math.min(3, scale + delta));

      // Adjust position to keep image centered when scaling
      if (newScale !== scale) {
        const rect = containerRef.current.getBoundingClientRect();
        const imageWidth = imageRef.current.naturalWidth;
        const imageHeight = imageRef.current.naturalHeight;

        const scaleRatio = newScale / scale;
        setPosition({
          x: position.x * scaleRatio,
          y: position.y * scaleRatio
        });
        setScale(newScale);
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        container.removeEventListener('wheel', handleWheel);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, startPos, position, scale]);

  // Reset position and scale when image changes
  useEffect(() => {
    // console.log("selectedImage", selectedImage);
    setIsOpen(selectedImage.alt !== 'Select a Product Image');
    setPosition({ x: 0, y: 0 });
    setScale(1);
  }, [selectedImage]);

  useEffect(() => {
    if (imageRef.current && imageRef.current.src === '') {
      setIsOpen(false);
    }
  }, [imageRef]);

  // Get aspect ratio display name
  const getAspectRatioName = (ratio) => {
    const ratioMap = {
      [1/1]: '1:1 (Square)',
      [16/9]: '16:9 (Widescreen)',
      [4/3]: '4:3 (Standard)',
      [3/4]: '3:4 (Portrait)',
      [9/16]: '9:16 (Mobile)'
    };
    return ratioMap[ratio] || `${ratio.toFixed(2)}:1`;
  };

  return (
    <div className="flex bg-gray-100">
      <div className="bg-white border border-gray-300 rounded-lg p-3 w-full flex flex-col items-center">
        <div className="w-full">
          <div
            className="flex justify-between items-center cursor-pointer w-full"
            onClick={toggleDropdown}
          >
            <div className="flex items-center">
              <span className="text-gray-700 font-semibold text-sm tracking-wide">Adjust Position</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInfo(!showInfo);
                }}
                className="text-gray-400 hover:text-purple-500 transition-all duration-300 transform hover:scale-110 ml-2"
              >
                <FaInfoCircle size={16} />
              </button>
            </div>
            <div className="flex items-center">
              {selectedImage && (
                <i
                  className="fas fa-undo-alt text-purple-600 hover:text-purple-700 mr-3 text-sm cursor-pointer transition-colors duration-200"
                  title="Reset position"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetImagePosition();
                  }}
                ></i>
              )}
              <i
                className={`fas ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                id="dropdown-icon"
              ></i>
            </div>
          </div>
          {showInfo && (
            <div
              className="mt-2 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 rounded-md shadow-md w-full transition-all duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start space-x-3">
                <i className="fas fa-lightbulb text-purple-500 mt-0.5"></i>
                <div className="text-sm text-gray-700 leading-relaxed">
                  <p>Modify the location and scale of the product image. Ensure the product remains within the designated area marked by the dotted line.</p>
                  <p className="mt-2 font-medium">Current Aspect Ratio: {getAspectRatioName(aspectRatio)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {isOpen && (
          <div className="w-full">
            <div
              ref={containerRef}
              id="dropdown-content"
              className="mt-4 border border-purple-500 border-dashed rounded-lg flex justify-center items-center relative overflow-hidden mx-auto"
              style={{
                width: '100%',
                aspectRatio: aspectRatio,
                maxWidth: '300px',
                margin: '1rem auto',
              }}
            >
              {selectedImage ? (
                <img
                  ref={imageRef}
                  id="adjustable-image"
                  alt={selectedImage.alt}
                  className="w-full h-full object-contain cursor-move"
                  src={selectedImage.src}
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transition: isDragging ? 'none' : 'transform 0.1s ease',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                  onMouseDown={handleMouseDown}
                />
              ) : (
                <div className="text-gray-500 text-center">
                  <p>No image selected</p>
                  <p className="text-sm">Select an image from the Products section</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdjustPositionDropdown;
